import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import JourneyBuilder from './JourneyBuilder';
import { fetchJourneys, createJourney, updateJourney } from '../../store/thunks/journeyThunk';
import { setSelectedJourney, clearSelectedJourney } from '../../store/slices/journeySlice';
import ClientJourneyProgressBar from '../../components/ui/ClientJourneyProgressBar';
import { toast } from 'react-toastify';

const defaultJourney = [
  { 
    name: 'Start', 
    color: '#1976d2', 
    steps: [{ 
      name: 'Initial Intake',
      description: 'Client first contact and basic information collection',
      isRequired: true,
      checkpoints: []
    }] 
  },
  { 
    name: 'Finish', 
    color: '#8e24aa', 
    steps: [{ 
      name: 'Case Closure',
      description: 'Final documentation and discharge process',
      isRequired: true,
      checkpoints: []
    }] 
  },
];

function JourneyManager({ clinicId }) {
  const dispatch = useDispatch();
  const { journeys = [], selectedJourney, status } = useSelector(state => state.journeys || {});
  const [isEditing, setIsEditing] = useState(false);
  const [localSections, setLocalSections] = useState(defaultJourney);
  const [editingId, setEditingId] = useState(null);
  const [journeyTitle, setJourneyTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (clinicId) dispatch(fetchJourneys(clinicId));
  }, [clinicId, dispatch]);

  // When editing a journey, load its sections and title
  useEffect(() => {
    if (selectedJourney) {
      setLocalSections(selectedJourney.sections);
      setEditingId(selectedJourney._id);
      setJourneyTitle(selectedJourney.name || '');
    }
  }, [selectedJourney]);

  // Start editing a journey
  const handleEdit = (journey) => {
    dispatch(setSelectedJourney(journey));
    setIsEditing(true);
  };

  // Start creating a new journey
  const handleCreate = () => {
    dispatch(clearSelectedJourney());
    setLocalSections(defaultJourney);
    setEditingId(null);
    setJourneyTitle('');
    setIsEditing(true);
  };

  // Save journey (create or update)
  const handleSave = async () => {
    // Validation
    if (!journeyTitle.trim()) {
      toast.warning('Please enter a journey title');
      return;
    }

    // Validate sections and steps
    for (let i = 0; i < localSections.length; i++) {
      const section = localSections[i];
      if (!section.name.trim()) {
        toast.warning(`Please enter a name for section ${i + 1}`);
        return;
      }
      
      for (let j = 0; j < section.steps.length; j++) {
        const step = section.steps[j];
        if (!step.name.trim()) {
          toast.warning(`Please enter a name for step ${j + 1} in section "${section.name}"`);
          return;
        }
      }
    }

    setIsSaving(true);

    const journeyData = {
      name: journeyTitle.trim(),
      sections: localSections.map(section => ({
        ...section,
        name: section.name.trim(),
        steps: section.steps.map(step => ({
          ...step,
          name: step.name.trim(),
          description: step.description || '',
          checkpoints: step.checkpoints || []
        }))
      })),
      clinicId,
    };

    try {
      if (editingId) {
        await dispatch(updateJourney({ id: editingId, journeyData })).unwrap();
      } else {
        await dispatch(createJourney(journeyData)).unwrap();
      }
      // Only close and reset if save was successful
      setIsEditing(false);
      dispatch(clearSelectedJourney());
    } catch (error) {
      console.error('Error saving journey:', error);
      toast.error('Failed to save journey. Please check all fields and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    dispatch(clearSelectedJourney());
  };

  // Placeholder for copy/import
  const handleCopy = () => {
    toast.info('Copy/import from another clinic coming soon!');
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {!isEditing ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Journeys</h2>
            <div className="flex gap-2">
              <button onClick={handleCopy} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Copy from another clinic</button>
              <button onClick={handleCreate} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">+ Create New Journey</button>
            </div>
          </div>
          {status === 'loading' && <div className="text-gray-500">Loading...</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!Array.isArray(journeys) || journeys.length === 0 ? (
              <div className="col-span-2 text-center text-gray-400 py-12 border rounded bg-gray-50">No journeys found for this clinic.</div>
            ) : journeys.map(journey => (
              <div key={journey._id} className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-lg border border-gray-100" onClick={() => handleEdit(journey)}>
                <div className="font-bold text-lg mb-1">{journey.name}</div>
                <div className="text-sm text-gray-500">{journey.sections.length} sections, {journey.sections.reduce((acc, s) => acc + s.steps.length, 0)} steps</div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {journey.sections.map((section, idx) => (
                    <span key={idx} className="px-2 py-1 rounded text-xs" style={{ background: section.color + '22', color: section.color }}>{section.name}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="relative bg-white rounded-lg shadow p-6">
          <button onClick={handleCancel} className="absolute top-4 right-4 text-gray-400 hover:text-red-500 text-2xl">×</button>
          <ClientJourneyProgressBar
            currentStep={localSections[0]?.steps?.[0] || ""}
            sections={localSections.map(s => ({
              name: s.name,
              color: s.color,
              substeps: s.steps,
            }))}
          />
          <JourneyBuilder
            sections={localSections}
            setSections={setLocalSections}
            journeyTitle={journeyTitle}
            setJourneyTitle={setJourneyTitle}
          />
          <div className="flex justify-end mt-6 gap-2">
            <button 
              onClick={handleCancel} 
              disabled={isSaving}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave} 
              disabled={isSaving}
              className={`px-4 py-2 rounded transition-colors ${
                isSaving 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default JourneyManager;